/**
 * Vakya Horoscope Matching Service (React/Web Version)
 * 
 * Ported from: d:\ionic\paranthaman5.0\src\app\services\vakya-horoscope-matching.service.ts
 * 
 * Full astronomical 12 Porutham matching with:
 *  - DOB, TOB, POB based calculation
 *  - astronomy-engine planetary positions
 *  - 12 Poruthams, Dosha Samya, Dasa Sandhi
 *  - Rasi & Navamsa chart rendering
 *  - PDF-ready HTML output via generateMatchingHTML
 */

import * as Astronomy from 'astronomy-engine';
import { generateMatchingHTML } from './horoscope-pdf-template';

// ─────────────────────────── CONSTANTS ────────────────────────────────────────

const NAKSHATRAS = [
    'ASWINI', 'BHARANI', 'KRITHIKA', 'ROHINI', 'MRIGASIR', 'ARDRA', 'PUNARVAS',
    'PUSHYAMI', 'ASHLESHA', 'MAKHA', 'PUBBA', 'UTTARA', 'HASTA', 'CHITRA',
    'SWATHI', 'VISAKHA', 'ANURADHA', 'JYESHTA', 'MOOLA', 'PURVASHD', 'UTRASADA',
    'SHRAVANA', 'DHANISTA', 'SATABISA', 'PURVBDRA', 'UTRABDRA', 'REVATHI'
];

const RASIS = [
    'MESHA', 'RISHABA', 'MITHUNA', 'KATAKA', 'SIMHA', 'KANYA',
    'THULA', 'VRICHIK', 'DHANUS', 'MAKARA', 'KUMBHA', 'MEENA'
];

const RASI_LORDS = ['KUJA', 'SUKR', 'BUDH', 'CHAN', 'SURY', 'BUDH', 'SUKR', 'KUJA', 'GURU', 'SANI', 'SANI', 'GURU'];

const NAKSHATRA_LORDS = ['KETU', 'SUKR', 'SURY', 'CHAN', 'KUJA', 'RAHU', 'GURU', 'SANI', 'BUDH'];
const DASA_YEARS = { KETU: 7, SUKR: 20, SURY: 6, CHAN: 10, KUJA: 7, RAHU: 18, GURU: 16, SANI: 19, BUDH: 17 };

// Ganam: 0=Deva, 1=Manushya, 2=Rakshasa
const GANAM = [0, 1, 2, 0, 0, 1, 0, 0, 2, 2, 1, 1, 0, 2, 0, 2, 0, 2, 2, 1, 1, 0, 2, 2, 1, 1, 0];
const GANAM_NAMES = ['DEVA', 'MANUSHYA', 'RAKSHASA'];

// Yoni animals
const YONI_ANIMALS = [
    'HORSE', 'ELEPHANT', 'SHEEP', 'SERPENT', 'SERPENT', 'DOG', 'CAT', 'SHEEP', 'CAT',
    'RAT', 'RAT', 'COW', 'BUFFALO', 'TIGER', 'BUFFALO', 'TIGER', 'DEER', 'DEER',
    'DOG', 'MONKEY', 'MONGOOSE', 'MONKEY', 'LION', 'HORSE', 'LION', 'COW', 'ELEPHANT'
];
const YONI_ENEMIES = [['HORSE', 'BUFFALO'], ['ELEPHANT', 'LION'], ['SHEEP', 'MONKEY'], ['SERPENT', 'MONGOOSE'], ['DOG', 'DEER'], ['CAT', 'RAT'], ['COW', 'TIGER']];

// Rajju: 0=Paada, 1=Kati, 2=Ooru/Uru, 3=Kantha, 4=Siro
const RAJJU = [0, 1, 2, 3, 4, 4, 3, 2, 1, 0, 1, 2, 3, 4, 4, 3, 2, 1, 0, 1, 2, 3, 4, 4, 3, 2, 1];
const RAJJU_NAMES = ['PADA', 'KATI', 'URU', 'KANDA', 'SIRO'];

// Vedhai pairs
const VEDHAI_PAIRS = [[0, 17], [1, 16], [2, 15], [3, 14], [4, 13], [5, 21], [6, 20], [7, 19], [8, 18], [9, 26], [10, 25], [11, 24], [12, 23]];

// Varna by RASI: 0=Brahmin (Water), 1=Kshatriya (Fire), 2=Vaishya (Earth), 3=Shudra (Air)
const RASI_VARNA = [1, 2, 3, 0, 1, 2, 3, 0, 1, 2, 3, 0];
const VARNA_NAMES = ['BRAHMIN', 'KSHATRIA', 'VAISHYA', 'SHUDRA'];

// Nadi: 0=Left(First), 1=Central(Madhya), 2=Right(Last)
const NADI = [
    0, 1, 2,  // Aswini, Bharani, Krithika
    2, 1, 0,  // Rohini, Mrigasir, Ardra
    0, 1, 2,  // Punarvasu, Pushyami, Ashlesha
    2, 1, 0,  // Makha, Pubba, Uttara
    0, 1, 2,  // Hasta, Chitra, Swathi
    2, 1, 0,  // Visakha, Anuradha, Jyeshta
    0, 1, 2,  // Moola, PurvAshadha, UtrAshadha
    2, 1, 0,  // Shravana, Dhanista, Satabisa
    0, 1, 2   // PurvBdra, UtrBdra, Revathi
];
const NADI_NAMES = ['LEFT', 'CENTRAL', 'RIGHT'];

// Vasiyam table
const VASIYAM = { 0: [4, 7], 1: [3, 6], 2: [5], 3: [7, 8], 4: [9], 5: [1, 11], 6: [9], 7: [3, 5], 8: [11], 9: [10], 10: [11], 11: [9] };

// Friendship table
const FRIENDSHIP = {
    SURY: { friends: ['CHAN', 'KUJA', 'GURU'], neutral: ['BUDH'], enemies: ['SUKR', 'SANI'] },
    CHAN: { friends: ['SURY', 'BUDH'], neutral: ['KUJA', 'GURU', 'SUKR', 'SANI'], enemies: [] },
    KUJA: { friends: ['SURY', 'CHAN', 'GURU'], neutral: ['SUKR', 'SANI'], enemies: ['BUDH'] },
    BUDH: { friends: ['SURY', 'SUKR'], neutral: ['KUJA', 'GURU', 'SANI'], enemies: ['CHAN'] },
    GURU: { friends: ['SURY', 'CHAN', 'KUJA'], neutral: ['SANI'], enemies: ['BUDH', 'SUKR'] },
    SUKR: { friends: ['BUDH', 'SANI'], neutral: ['KUJA', 'GURU'], enemies: ['SURY', 'CHAN'] },
    SANI: { friends: ['BUDH', 'SUKR'], neutral: ['GURU'], enemies: ['SURY', 'CHAN', 'KUJA'] }
};

// ─────────────────────────── CALCULATION HELPERS ─────────────────────────────

function calcAyanamsa(year) {
    // Calibrated to match original ASTRO Y2K software
    return 22.458985 + (year - 1900) * 50.2564 / 3600;
}

function getNakIdx(sidLon) { return Math.floor(((sidLon % 360 + 360) % 360) / (360 / 27)); }
function getRasiIdx(sidLon) { return Math.floor(((sidLon % 360 + 360) % 360) / 30); }
function getNavamsaIdx(sidLon) { return Math.floor(((sidLon % 360 + 360) % 360) / (360 / 108)) % 12; }

function calcRahu(astroTime) {
    const T = astroTime.tt / 36525.0;
    let omega = 125.04452 - 1934.136261 * T + 0.0020708 * T * T;
    return ((omega % 360) + 360) % 360;
}

function getTropLon(body, astroTime) {
    if (body === 'Sun') return Astronomy.SunPosition(astroTime).elon;
    if (body === 'Moon') return Astronomy.EclipticGeoMoon(astroTime).lon;
    return Astronomy.Ecliptic(Astronomy.GeoVector(body, astroTime, true)).elon;
}

function calcAscendant(lstDeg, latDeg) {
    const lat = latDeg * Math.PI / 180, obl = 23.4393 * Math.PI / 180, lst = lstDeg * Math.PI / 180;
    const y = Math.cos(lst), x = -(Math.sin(lst) * Math.cos(obl) + Math.tan(lat) * Math.sin(obl));
    return ((Math.atan2(y, x) * 180 / Math.PI) % 360 + 360) % 360;
}

function getPlanetState(planet, rasi) {
    const exalt = { SURY: 0, CHAN: 1, KUJA: 9, BUDH: 5, GURU: 3, SUKR: 11, SANI: 6 };
    const debil = { SURY: 6, CHAN: 7, KUJA: 3, BUDH: 11, GURU: 9, SUKR: 5, SANI: 0 };
    if (exalt[planet] === rasi) return 'UCHA';
    if (debil[planet] === rasi) return 'NICH';

    const lord = RASI_LORDS[rasi];
    if (lord === planet) return 'SUYA';

    let fDict = FRIENDSHIP[planet];
    if (!fDict && (planet === 'RAHU' || planet === 'KETU')) {
        fDict = { friends: ['SUKR', 'SANI'], neutral: ['BUDH', 'GURU'], enemies: ['SURY', 'CHAN', 'KUJA'] };
    }

    if (fDict) {
        if (fDict.friends.includes(lord)) return 'NATP';
        if (fDict.enemies.includes(lord)) return 'PAKA';
    }
    return 'SAMA';
}

// ─────────────────────────── FULL CHART CALCULATOR ───────────────────────────

function calcFullChart(input) {
    const { day, month, year, hour, minute, timezone, latDeg, latMin, latDir, lonDeg, lonMin, lonDir } = input;
    const lat = (latDeg + latMin / 60) * (latDir === 'S' ? -1 : 1);
    const lon = (lonDeg + lonMin / 60) * (lonDir === 'W' ? -1 : 1);

    const localMs = Date.UTC(year, month - 1, day, hour, minute, 0);
    const utcMs = localMs - timezone * 3600000;
    const birthDate = new Date(utcMs);
    const astroTime = Astronomy.MakeTime(birthDate);
    const fracYear = year + (month - 1) / 12 + (day - 1) / 365.25;
    const ayanamsa = calcAyanamsa(fracYear);

    // Planetary positions (sidereal)
    const sid = {};
    const bodies = { SURY: 'Sun', CHAN: 'Moon', KUJA: 'Mars', BUDH: 'Mercury', GURU: 'Jupiter', SUKR: 'Venus', SANI: 'Saturn', URAN: 'Uranus', NEPT: 'Neptune', PLUT: 'Pluto' };
    for (const [abbr, body] of Object.entries(bodies)) {
        sid[abbr] = ((getTropLon(body, astroTime) - ayanamsa) % 360 + 360) % 360;
    }
    sid.RAHU = ((calcRahu(astroTime) - ayanamsa) % 360 + 360) % 360;
    sid.KETU = (sid.RAHU + 180) % 360;

    // Lagna
    const gmst = Astronomy.SiderealTime(astroTime);
    const lstDeg = ((gmst * 15 + lon) % 360 + 360) % 360;
    sid.LAGN = ((calcAscendant(lstDeg, lat) - ayanamsa) % 360 + 360) % 360;

    // Gulika
    const observer = new Astronomy.Observer(lat, lon, 0);
    const dayStart = new Date(Date.UTC(year, month - 1, day, 0, 0, 0) - timezone * 3600000);
    const sr = Astronomy.SearchRiseSet('Sun', observer, +1, Astronomy.MakeTime(dayStart), 1);
    const ss = Astronomy.SearchRiseSet('Sun', observer, -1, Astronomy.MakeTime(dayStart), 1);
    if (sr && ss) {
        const srLocal = new Date(sr.date.getTime() + timezone * 3600000);
        const ssLocal = new Date(ss.date.getTime() + timezone * 3600000);
        const sunriseFrac = (srLocal.getUTCHours() + srLocal.getUTCMinutes() / 60) / 24;
        const sunsetFrac = (ssLocal.getUTCHours() + ssLocal.getUTCMinutes() / 60) / 24;
        const weekday = new Date(birthDate.getTime() + timezone * 3600000).getDay();
        const dayDur = (sunsetFrac - sunriseFrac) * 24;
        const gulikaHoraIndex = [6, 5, 4, 3, 2, 1, 0];
        const horaLen = dayDur / 8;
        const gulikaHrs = sunriseFrac * 24 + gulikaHoraIndex[weekday] * horaLen;
        const gulikaDate = new Date(dayStart.getTime() + timezone * 3600000);
        gulikaDate.setUTCHours(0, 0, 0, 0);
        gulikaDate.setTime(gulikaDate.getTime() + gulikaHrs * 3600000 - timezone * 3600000);
        const gAt = Astronomy.MakeTime(gulikaDate);
        const gGmst = Astronomy.SiderealTime(gAt);
        const gLstDeg = ((gGmst * 15 + lon) % 360 + 360) % 360;
        sid.GULI = ((calcAscendant(gLstDeg, lat) - ayanamsa) % 360 + 360) % 360;
    } else {
        sid.GULI = 0;
    }

    // Planet Rasi/Navamsa positions
    const rasiPlanets = {};
    const navamsaPlanets = {};
    for (let i = 0; i < 12; i++) { rasiPlanets[i] = []; navamsaPlanets[i] = []; }
    const allP = ['SURY', 'CHAN', 'KUJA', 'BUDH', 'GURU', 'SUKR', 'SANI', 'RAHU', 'KETU', 'LAGN', 'GULI', 'URAN', 'NEPT', 'PLUT'];
    for (const p of allP) {
        rasiPlanets[getRasiIdx(sid[p])].push(p);
        navamsaPlanets[getNavamsaIdx(sid[p])].push(p);
    }

    // Moon details
    const moonNak = getNakIdx(sid.CHAN);
    const moonRasi = getRasiIdx(sid.CHAN);

    // Dasa info
    const nakLordIdx = moonNak % 9;
    const nakStart = moonNak * (360 / 27);
    const elapsed = sid.CHAN - nakStart;
    const remainFrac = 1 - elapsed / (360 / 27);

    return { sid, rasiPlanets, navamsaPlanets, moonNak, moonRasi, birthDate, ayanamsa, nakLordIdx, remainFrac };
}

// ─────────────────────────── CHART RENDERING ─────────────────────────────────

function renderChart(planetsByRasi, title) {
    const layout = [[11, 0, 1, 2], [10, -1, -1, 3], [9, -1, -1, 4], [8, 7, 6, 5]];
    const cw = 9;
    const lines = [];
    lines.push('+' + ('--------+').repeat(4));
    for (let row = 0; row < 4; row++) {
        const l1 = [], l2 = [];
        for (let col = 0; col < 4; col++) {
            const ri = layout[row][col];
            if (ri === -1) {
                if (row === 1 && col === 1) { l1.push(' '.repeat(cw * 2 - 1)); l2.push(' '.repeat(cw * 2 - 1)); col++; continue; }
            }
            const pl = (planetsByRasi[ri] || []).map(p => p.substring(0, 3));
            let s1 = pl.slice(0, 2).join(' ');
            let s2 = pl.slice(2, 4).join(' ');
            if (pl.length > 4) {
                s1 = pl.slice(0, 2).join(' ') + pl.length;
            }
            l1.push(s1.padEnd(cw - 1).substring(0, cw - 1));
            l2.push(s2.padEnd(cw - 1).substring(0, cw - 1));
        }
        lines.push('|' + l1.join('|') + '|');
        lines.push('|' + l2.join('|') + '|');
        if (row === 0 || row === 2) {
            lines.push('+' + '--------+' + ' '.repeat(cw * 2 - 1) + '+' + '--------+');
        } else if (row === 1) {
            const tp = ('    ' + title).padEnd(cw * 2 - 1);
            lines.push('+' + '--------+' + tp + '+' + '--------+');
        } else {
            lines.push('+' + ('--------+').repeat(4));
        }
    }
    return lines;
}

// ─────────────────────────── 12 PORUTHAMS ────────────────────────────────────

function check12Poruthams(boyNak, boyRasi, girlNak, girlRasi) {
    const results = [];

    // 1. DINA - Count from GIRL's star to BOY's star (girl's star = 1)
    const dinaCount = ((boyNak - girlNak) % 27 + 27) % 27 + 1;
    let dinaRem = dinaCount % 9;
    if (dinaRem === 0) dinaRem = 9;
    const dinaPass = ![3, 5, 7].includes(dinaRem);
    results.push({ name: 'DINA', boyVal: NAKSHATRAS[boyNak], girlVal: NAKSHATRAS[girlNak], match: dinaPass ? 'GOOD' : 'POOR', points: dinaPass ? 1 : 0 });

    // 2. GANA
    const bg = GANAM[boyNak], gg = GANAM[girlNak];
    let ganaPass = bg === gg || (bg <= 1 && gg <= 1);
    results.push({ name: 'GANA', boyVal: GANAM_NAMES[bg], girlVal: GANAM_NAMES[gg], match: ganaPass ? 'GOOD' : 'POOR', points: ganaPass ? 1 : 0 });

    // 3. MAHENDRA
    const mahCount = ((girlNak - boyNak) % 27 + 27) % 27 + 1;
    const mahPass = [4, 7, 10, 13, 16, 19, 22, 25].includes(mahCount);
    results.push({ name: 'MAHENDRA', boyVal: '', girlVal: '', match: mahPass ? 'GOOD' : 'POOR', points: mahPass ? 1 : 0 });

    // 4. STHREE DHIRGA
    const sdCount = ((boyNak - girlNak) % 27 + 27) % 27 + 1;
    const sdPass = sdCount > 15;
    results.push({ name: 'STHREE DHIRGA', boyVal: '', girlVal: '', match: sdPass ? 'GOOD' : 'POOR', points: sdPass ? 1 : 0 });

    // 5. YONI
    const ba = YONI_ANIMALS[boyNak], ga = YONI_ANIMALS[girlNak];
    const yoniEnemy = YONI_ENEMIES.some(p => (p[0] === ba && p[1] === ga) || (p[1] === ba && p[0] === ga));
    const yoniSame = ba === ga;
    let yoniMatch = 'FAIR';
    if (yoniEnemy) yoniMatch = 'POOR';
    else if (yoniSame || ['TIGER', 'SHEEP', 'DEER', 'DOG'].includes(ba) && ga === 'LION') yoniMatch = 'GOOD';
    results.push({ name: 'YONI', boyVal: ba, girlVal: ga, match: yoniMatch, points: yoniMatch !== 'POOR' ? 1 : 0 });

    // 6. RASI
    const rasiDist = ((boyRasi - girlRasi) % 12 + 12) % 12 + 1;
    let rasiMatch = 'POOR';
    if (rasiDist > 6 || rasiDist === 1) rasiMatch = 'GOOD';
    else if (rasiDist === 4 || rasiDist === 5) rasiMatch = 'FAIR';
    results.push({ name: 'RASI', boyVal: RASIS[boyRasi], girlVal: RASIS[girlRasi], match: rasiMatch, points: rasiMatch !== 'POOR' ? 1 : 0 });

    // 7. ADIPATHY (Rasiyathipathi)
    const bl = RASI_LORDS[boyRasi], gl = RASI_LORDS[girlRasi];
    let adipathiMatch = 'GOOD';
    if (bl !== gl) {
        const f1 = FRIENDSHIP[bl], f2 = FRIENDSHIP[gl];
        if (f1 && f2) {
            if (f1.enemies.includes(gl) && f2.enemies.includes(bl)) adipathiMatch = 'POOR';
            else if (f1.enemies.includes(gl) || f2.enemies.includes(bl)) adipathiMatch = 'FAIR';
        }
    }
    const blName = bl === 'CHAN' ? 'CHANDRA' : bl === 'SURY' ? 'SURYA' : bl === 'SUKR' ? 'SUKRA' : bl === 'BUDH' ? 'BUDHA' : bl;
    const glName = gl === 'CHAN' ? 'CHANDRA' : gl === 'SURY' ? 'SURYA' : gl === 'SUKR' ? 'SUKRA' : gl === 'BUDH' ? 'BUDHA' : gl;
    results.push({ name: 'ADIPATHY', boyVal: blName, girlVal: glName, match: adipathiMatch, points: adipathiMatch !== 'POOR' ? 1 : 0 });

    // 8. VASYA
    const bVas = VASIYAM[boyRasi] || [];
    const gVas = VASIYAM[girlRasi] || [];
    const vasyaPass = boyRasi === girlRasi || bVas.includes(girlRasi) || gVas.includes(boyRasi);
    results.push({ name: 'VASYA', boyVal: '', girlVal: '', match: vasyaPass ? 'GOOD' : 'POOR', points: vasyaPass ? 1 : 0 });

    // 9. RAJJU
    const br = RAJJU[boyNak], gr = RAJJU[girlNak];
    const rajjuPass = br !== gr;
    results.push({ name: 'RAJJU', boyVal: RAJJU_NAMES[br], girlVal: RAJJU_NAMES[gr], match: rajjuPass ? 'GOOD' : 'POOR', points: rajjuPass ? 1 : 0 });

    // 10. VEDHA
    const vedhaHit = VEDHAI_PAIRS.some(p => (p[0] === boyNak && p[1] === girlNak) || (p[1] === boyNak && p[0] === girlNak));
    results.push({ name: 'VEDHA', boyVal: '', girlVal: '', match: !vedhaHit ? 'GOOD' : 'POOR', points: !vedhaHit ? 1 : 0 });

    // 11. VARNA
    const bv = RASI_VARNA[boyRasi], gv = RASI_VARNA[girlRasi];
    const varnaPass = bv <= gv || bv === gv;
    results.push({ name: 'VARNA', boyVal: VARNA_NAMES[bv], girlVal: VARNA_NAMES[gv], match: varnaPass ? 'GOOD' : 'POOR', points: varnaPass ? 1 : 0 });

    // 12. NADI
    const bn = NADI[boyNak], gn = NADI[girlNak];
    const nadiPass = bn !== gn;
    let nadiMatch = nadiPass ? 'GOOD' : 'POOR';
    if (!nadiPass && boyNak === 5 && girlNak === 24) nadiMatch = 'FAIR';
    results.push({ name: 'NADI', boyVal: NADI_NAMES[bn], girlVal: NADI_NAMES[gn], match: nadiMatch, points: nadiMatch !== 'POOR' ? 1 : 0 });

    return results;
}

// ─────────────────────────── DOSHA SAMYA ─────────────────────────────────────

function getDoshaValue(planet, planetLon, planetRasi, refLon, refRasi, state, refName) {
    const house = ((planetRasi - refRasi) % 12 + 12) % 12 + 1;
    if (![2, 4, 7, 8, 12].includes(house)) return 0.0;

    const pDeg = ((planetLon % 360) + 360) % 360;
    const posInSign = pDeg % 30;
    const frac = (posInSign / 30);
    const weights = { 2: 0.5, 4: 0.4, 7: 0.7, 8: 0.8, 12: 0.6 };
    let baseVal = frac * (weights[house] || 0.5);

    if (state === 'SUYA') baseVal *= 0.625;
    else if (state === 'NATP' || state === 'MITRA') baseVal *= 0.375;
    else if (state === 'UCHA') baseVal *= 0.4222;

    return parseFloat(baseVal.toFixed(2));
}

function calcDoshaSamya(chart) {
    const malefics = ['SURY', 'KUJA', 'SANI', 'RAHU', 'KETU'];
    const doshaRows = [];
    const moonRasi = getRasiIdx(chart.sid.CHAN);
    const sukrRasi = getRasiIdx(chart.sid.SUKR);
    const lagnRasi = getRasiIdx(chart.sid.LAGN);

    for (const p of malefics) {
        const pLon = chart.sid[p];
        const pRasi = getRasiIdx(pLon);
        const state = getPlanetState(p, pRasi);

        const guruRasi = getRasiIdx(chart.sid.GURU);
        const houseFromGuru = ((pRasi - guruRasi) % 12 + 12) % 12 + 1;
        const guruAspects = [5, 7, 9].includes(houseFromGuru) || pRasi === guruRasi;

        let moonDosha = 0, sukrDosha = 0, lagnDosha = 0;

        if (!guruAspects) {
            moonDosha = getDoshaValue(p, pLon, pRasi, chart.sid.CHAN, moonRasi, state, 'MOON');
            sukrDosha = getDoshaValue(p, pLon, pRasi, chart.sid.SUKR, sukrRasi, state, 'SUKR');
            lagnDosha = getDoshaValue(p, pLon, pRasi, chart.sid.LAGN, lagnRasi, state, 'LAGN');
        }
        const total = moonDosha + sukrDosha + lagnDosha;

        const fmt = (v) => v === 0 ? '  .00' : Number(v).toFixed(2).replace('0.', '  .');
        const sumFmt = (v) => v === 0 ? '  .00' : Number(v).toFixed(2).replace('0.', ' .');

        let disp = state;
        if (state === 'SUYA') disp = 'SWAKS';
        else if (state === 'NATP') disp = 'MITRA';
        else if (state === 'PAKA') disp = 'SATRU';
        else if (state === 'UCHA') disp = 'UCCHA';
        else if (state === 'NICH') disp = 'NEECHA';

        doshaRows.push({
            planet: p.padEnd(8),
            state: disp.padEnd(7),
            moon: fmt(moonDosha),
            sukr: fmt(sukrDosha),
            lagn: fmt(lagnDosha),
            total: sumFmt(total)
        });
    }
    return doshaRows;
}

// ─────────────────────────── DASA SANDHI ─────────────────────────────────────

function getDasaSequence(chart) {
    const dasaSeq = [];
    const birthMs = chart.birthDate.getTime();
    const nakLord = NAKSHATRA_LORDS[chart.nakLordIdx];
    const dasaYrs = DASA_YEARS[nakLord];
    const remainYrs = dasaYrs * chart.remainFrac;

    let currentMs = birthMs;
    let startIdx = chart.nakLordIdx;

    const firstDays = Math.round(remainYrs * 365.25);
    const firstEnd = currentMs + firstDays * 86400000;
    dasaSeq.push({ lord: nakLord, start: currentMs, end: firstEnd });
    currentMs = firstEnd;

    for (let i = 1; i <= 8; i++) {
        const nIdx = (startIdx + i) % 9;
        const lord = NAKSHATRA_LORDS[nIdx];
        const days = Math.round(DASA_YEARS[lord] * 365.25);
        const endMs = currentMs + days * 86400000;
        dasaSeq.push({ lord, start: currentMs, end: endMs });
        currentMs = endMs;
    }
    return dasaSeq;
}

function checkDasaSandhiOverlap(boyChart, girlChart) {
    const boyDasa = getDasaSequence(boyChart);
    const girlDasa = getDasaSequence(girlChart);

    for (let i = 0; i < boyDasa.length - 1; i++) {
        const bdDate = boyDasa[i].end;
        for (let j = 0; j < girlDasa.length - 1; j++) {
            const gdDate = girlDasa[j].end;
            const diffDays = Math.abs(bdDate - gdDate) / 86400000;
            if (diffDays <= 365.25) {
                return true;
            }
        }
    }
    return false;
}

// ─────────────────────────── MAIN MATCHING FUNCTION ──────────────────────────

function generateMatching(boyInput, girlInput) {
    const boyChart = calcFullChart(boyInput);
    const girlChart = calcFullChart(girlInput);

    const output = [];
    const today = new Date();
    const todayStr = `${String(today.getDate()).padStart(2)}/${String(today.getMonth() + 1).padStart(2, ' ')}/${today.getFullYear()}`;

    // Header
    output.push(` (VAKYA JS/01)         HOROSCOPE MATCHING                         ${todayStr}`);
    output.push('');
    output.push('                 BOY                             GIRL');
    output.push(` NAME          : ${boyInput.name.padEnd(28)}  ${girlInput.name}`);
    output.push(` PLACE OF BIRTH: ${boyInput.place.padEnd(28)}  ${girlInput.place}`);

    const boyDateStr = `${String(boyInput.day).padStart(2)}-${String(boyInput.month).padStart(2, ' ')}-${boyInput.year}`;
    const girlDateStr = `${String(girlInput.day).padStart(2)}-${String(girlInput.month).padStart(2, ' ')}-${girlInput.year}`;
    const boyTimeStr = `${String(boyInput.hour).padStart(2)}:${String(boyInput.minute).padStart(2, '0')}`;
    const girlTimeStr = `${String(girlInput.hour).padStart(2)}:${String(girlInput.minute).padStart(2, '0')}`;
    const boyTzStr = boyInput.tzAbbr || 'IST';
    const girlTzStr = girlInput.tzAbbr || 'IST';
    output.push(` DATE & TIME   : ${boyDateStr} ${boyTimeStr}${boyTzStr}             ${girlDateStr} ${girlTimeStr}${girlTzStr}`);
    output.push('');

    // Side-by-side Rasi charts
    const boyRasi = renderChart(boyChart.rasiPlanets, 'RASI-BOY');
    const girlRasi = renderChart(girlChart.rasiPlanets, 'RASI-GIRL');
    for (let i = 0; i < boyRasi.length; i++) {
        output.push(' ' + (boyRasi[i] || '').padEnd(39) + ' ' + (girlRasi[i] || ''));
    }

    // Side-by-side Navamsa charts
    const boyAmsa = renderChart(boyChart.navamsaPlanets, 'AMSA-BOY');
    const girlAmsa = renderChart(girlChart.navamsaPlanets, 'AMSA-GIRL');
    for (let i = 0; i < boyAmsa.length; i++) {
        output.push(' ' + (boyAmsa[i] || '').padEnd(39) + ' ' + (girlAmsa[i] || ''));
    }

    // 12 Poruthams
    const poruthams = check12Poruthams(boyChart.moonNak, boyChart.moonRasi, girlChart.moonNak, girlChart.moonRasi);

    output.push(' KUTAS       GUNA           BOY         GIRL         MATCH   POINTS');
    output.push(' (PORUTHAMS) -------------------------------------------------------');

    for (let i = 0; i < poruthams.length; i++) {
        const p = poruthams[i];
        const num = String(i + 1).padStart(2);
        const name = p.name.padEnd(14);
        const bv = p.boyVal.padEnd(12);
        const gv = p.girlVal.padEnd(13);
        output.push(`           ${num}) ${name}${bv}${gv}${p.match.padEnd(8)} ${p.points}`);
    }

    const totalPoints = poruthams.reduce((s, p) => s + p.points, 0);
    output.push(`                                                     TOTAL       ${totalPoints}/12`);

    // Dosha Samya
    output.push('\f');
    output.push(' DOSHA SAMYAS ( No Dosha, if Guru Aspects)');
    output.push(' ============ -------------BOY-------------     *************GIRL************');
    output.push(' Planet       STATE  MOON  SUKR  LAGN TOTAL     STATE  MOON  SUKR  LAGN TOTAL');

    const boyDosha = calcDoshaSamya(boyChart);
    const girlDosha = calcDoshaSamya(girlChart);

    let boyTotM = 0, boyTotS = 0, boyTotL = 0;
    let girlTotM = 0, girlTotS = 0, girlTotL = 0;

    for (let i = 0; i < boyDosha.length; i++) {
        const b = boyDosha[i], g = girlDosha[i];
        const bm = parseFloat(b.moon), bs = parseFloat(b.sukr), bl2 = parseFloat(b.lagn);
        const gm = parseFloat(g.moon), gs = parseFloat(g.sukr), gl2 = parseFloat(g.lagn);
        boyTotM += bm; boyTotS += bs; boyTotL += bl2;
        girlTotM += gm; girlTotS += gs; girlTotL += gl2;
        output.push(` ${b.planet}         ${b.state} ${b.moon} ${b.sukr} ${b.lagn} ${b.total}     ${g.state} ${g.moon} ${g.sukr} ${g.lagn} ${g.total}`);
    }

    output.push('                      ----  ----  ----  ----            ----  ----  ----  ----');
    output.push(`              TOTAL  ${boyTotM.toFixed(2).padStart(5)} ${boyTotS.toFixed(2).padStart(5)} ${boyTotL.toFixed(2).padStart(5)} ${(boyTotM + boyTotS + boyTotL).toFixed(2).padStart(5)}           ${girlTotM.toFixed(2).padStart(5)} ${girlTotS.toFixed(2).padStart(5)} ${girlTotL.toFixed(2).padStart(5)} ${(girlTotM + girlTotS + girlTotL).toFixed(2).padStart(5)}`);
    output.push('                      ----  ----  ----  ----            ----  ----  ----  ----');
    output.push('');

    // Dasa Sandhi summary
    const hasDasaSandhi = checkDasaSandhiOverlap(boyChart, girlChart);
    if (hasDasaSandhi) {
        output.push('  DASA SANDHI OCCURS,BUT NO BUKTHI SANDHI;OK');
    } else {
        output.push('  DASA SANDHI DOES NOT OCCUR. ACCEPTABLE');
    }

    // Result summary
    output.push('');
    if (totalPoints >= 10) {
        output.push('Result: The Jadhaka Porutham is found good in all respect.');
    } else if (totalPoints >= 7) {
        output.push('Result: The Jadhaka Porutham is found generally good.');
    } else {
        output.push('Result: The Jadhaka Porutham shows some incompatibilities.');
    }
    if (poruthams[8].points === 0) {
        output.push(`Rajju Dosha is present (both in ${poruthams[8].boyVal} Rajju).`);
    }
    if (poruthams[11].points === 0) {
        output.push(`Nadi Dosha is present (both in ${poruthams[11].boyVal} Nadi).`);
    }
    output.push('');

    return {
        outputText: output.join('\n'),
        boyInfo: {
            name: boyInput.name,
            date: boyDateStr,
            time: boyTimeStr,
            place: boyInput.place,
            nakshatra: NAKSHATRAS[boyChart.moonNak],
            pada: Math.floor(((boyChart.sid.CHAN % 360) + 360) % 360 / (360 / 108)) % 4 + 1
        },
        girlInfo: {
            name: girlInput.name,
            date: girlDateStr,
            time: girlTimeStr,
            place: girlInput.place,
            nakshatra: NAKSHATRAS[girlChart.moonNak],
            pada: Math.floor(((girlChart.sid.CHAN % 360) + 360) % 360 / (360 / 108)) % 4 + 1
        },
        poruthams: poruthams.map(p => ({
            name: p.name,
            result: p.match,
            boyValue: p.boyVal,
            girlValue: p.girlVal,
            points: p.points
        })),
        totalScore: totalPoints,
        // Chart data for HTML rendering
        boyRasiPlanets: boyChart.rasiPlanets,
        boyNavamsaPlanets: boyChart.navamsaPlanets,
        girlRasiPlanets: girlChart.rasiPlanets,
        girlNavamsaPlanets: girlChart.navamsaPlanets,
        // Dosha Samya
        boyDosha: boyDosha,
        girlDosha: girlDosha,
        // Dasa Sandhi
        hasDasaSandhi: hasDasaSandhi,
        // Result summary
        resultSummary: totalPoints >= 10 ? 'good_all' : totalPoints >= 7 ? 'good_general' : 'incompatible',
        rajjuDosha: poruthams[8].points === 0 ? poruthams[8].boyVal : null,
        nadiDosha: poruthams[11].points === 0 ? poruthams[11].boyVal : null
    };
}

// ─────────────────────────── EXPORTED SERVICE CLASS ──────────────────────────

export class VakyaHoroscopeMatchingService {
    async calculateMatching(boyDetails, girlDetails, lang = 'ta') {
        try {
            const boyDateObj = new Date(boyDetails.date + 'T' + boyDetails.time);
            const girlDateObj = new Date(girlDetails.date + 'T' + girlDetails.time);

            const boyInput = {
                name: boyDetails.name,
                day: boyDateObj.getDate(),
                month: boyDateObj.getMonth() + 1,
                year: boyDateObj.getFullYear(),
                hour: boyDateObj.getHours(),
                minute: boyDateObj.getMinutes(),
                timezone: boyDetails.timezone,
                place: boyDetails.place,
                latDeg: Math.floor(Math.abs(boyDetails.latitude)),
                latMin: Math.round((Math.abs(boyDetails.latitude) % 1) * 60),
                latDir: boyDetails.latitude >= 0 ? 'N' : 'S',
                lonDeg: Math.floor(Math.abs(boyDetails.longitude)),
                lonMin: Math.round((Math.abs(boyDetails.longitude) % 1) * 60),
                lonDir: boyDetails.longitude >= 0 ? 'E' : 'W',
            };

            const girlInput = {
                name: girlDetails.name,
                day: girlDateObj.getDate(),
                month: girlDateObj.getMonth() + 1,
                year: girlDateObj.getFullYear(),
                hour: girlDateObj.getHours(),
                minute: girlDateObj.getMinutes(),
                timezone: girlDetails.timezone,
                place: girlDetails.place,
                latDeg: Math.floor(Math.abs(girlDetails.latitude)),
                latMin: Math.round((Math.abs(girlDetails.latitude) % 1) * 60),
                latDir: girlDetails.latitude >= 0 ? 'N' : 'S',
                lonDeg: Math.floor(Math.abs(girlDetails.longitude)),
                lonMin: Math.round((Math.abs(girlDetails.longitude) % 1) * 60),
                lonDir: girlDetails.longitude >= 0 ? 'E' : 'W',
            };

            const resultObj = generateMatching(boyInput, girlInput);

            const mResult = {
                formattedOutput: resultObj.outputText,
                htmlOutput: '',
                boyInfo: resultObj.boyInfo,
                girlInfo: resultObj.girlInfo,
                poruthams: resultObj.poruthams,
                totalScore: resultObj.totalScore,
                // Chart data
                boyRasiPlanets: resultObj.boyRasiPlanets,
                boyNavamsaPlanets: resultObj.boyNavamsaPlanets,
                girlRasiPlanets: resultObj.girlRasiPlanets,
                girlNavamsaPlanets: resultObj.girlNavamsaPlanets,
                // Dosha & Dasa
                boyDosha: resultObj.boyDosha,
                girlDosha: resultObj.girlDosha,
                hasDasaSandhi: resultObj.hasDasaSandhi,
                resultSummary: resultObj.resultSummary,
                rajjuDosha: resultObj.rajjuDosha,
                nadiDosha: resultObj.nadiDosha
            };

            mResult.htmlOutput = generateMatchingHTML(mResult, lang);

            return mResult;
        } catch (error) {
            console.error('Error calculating matching:', error);
            throw error;
        }
    }
}
